/**
 * Typed DOM queries. Passing an element constructor checks the match at runtime (`instanceof`), so the
 * returned type is guaranteed rather than asserted.
 */
type ElementType<E extends Element> = abstract new (...args: never[]) => E;

export function $(selector: string, root?: ParentNode): HTMLElement | null;
export function $<E extends Element>(selector: string, root: ParentNode, type: ElementType<E>): E | null;
export function $(
  selector: string,
  root: ParentNode = document,
  type: ElementType<Element> = HTMLElement,
): Element | null {
  const element = root.querySelector(selector);
  return element instanceof type ? element : null;
}

export function $$(selector: string, root?: ParentNode): HTMLElement[];
export function $$<E extends Element>(selector: string, root: ParentNode, type: ElementType<E>): E[];
export function $$(selector: string, root: ParentNode = document, type: ElementType<Element> = HTMLElement): Element[] {
  return Array.from(root.querySelectorAll(selector)).filter((element) => element instanceof type);
}

/** Like `$`, for elements the surrounding markup guarantees. Throws a descriptive error otherwise. */
export function requireElement(selector: string, root?: ParentNode): HTMLElement;
export function requireElement<E extends Element>(selector: string, root: ParentNode, type: ElementType<E>): E;
export function requireElement(
  selector: string,
  root: ParentNode = document,
  type: ElementType<Element> = HTMLElement,
): Element {
  const element = $(selector, root, type);
  if (!element) throw new Error(`Required element not found: ${selector}`);
  return element;
}

export const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const supportsIntersectionObserver = (): boolean => 'IntersectionObserver' in window;

/** The closest HTML element (ancestor or self) of an event's target matching the selector, or null. */
export function closestTarget(event: Event, selector: string): HTMLElement | null {
  const match = event.target instanceof Element ? event.target.closest(selector) : null;
  return match instanceof HTMLElement ? match : null;
}
